import { useEffect, useRef, useState, useCallback } from 'react';

import { useLocation } from 'react-router-dom';

import type { PopoverProps } from 'mastodon/components/popover';
import { Popover } from 'mastodon/components/popover';
import { StatusVisibilityHoverCard } from 'mastodon/components/status_visibility_hover_card';
import { useTimeout } from 'mastodon/hooks/useTimeout';

// Mirrors `hover_card_controller.tsx`'s tuning -- kept as a separate
// controller (rather than folded into that one) so this narrower feature
// stays independently understandable/removable.
const offset: PopoverProps['offset'] = { crossAxis: -12, mainAxis: 4 } as const;
const enterDelay = 400;
const leaveDelay = 150;
const activeMovementThreshold = 150;

const ANCHOR_SELECTOR = '[data-hover-card-status-visibility]';
const isHoverCardAnchor = (element: HTMLElement) =>
  element.matches(ANCHOR_SELECTOR);

export const StatusVisibilityHoverCardController: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [statusId, setStatusId] = useState<string | undefined>();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [setLeaveTimeout, cancelLeaveTimeout] = useTimeout();
  const [setEnterTimeout, cancelEnterTimeout, delayEnterTimeout] = useTimeout();
  const [setScrollTimeout] = useTimeout();
  const lastMouseMoveTime = useRef<number>(0);

  const handleClose = useCallback(() => {
    cancelEnterTimeout();
    cancelLeaveTimeout();
    setOpen(false);
    setAnchor(null);
  }, [cancelEnterTimeout, cancelLeaveTimeout, setOpen, setAnchor]);

  const location = useLocation();
  const [previousLocation, setPreviousLocation] = useState(location);
  if (location !== previousLocation) {
    setPreviousLocation(location);
    handleClose();
  }

  useEffect(() => {
    let isScrolling = false;
    let isUsingTouch = false;
    let currentAnchor: HTMLElement | null = null;

    const open = (target: HTMLElement) => {
      target.setAttribute('aria-describedby', 'visibility-hover-card');
      setOpen(true);
      setAnchor(target);
      setStatusId(
        target.getAttribute('data-hover-card-status-visibility') ?? undefined,
      );
    };

    const close = () => {
      currentAnchor?.removeAttribute('aria-describedby');
      currentAnchor = null;
      setOpen(false);
      setAnchor(null);
      setStatusId(undefined);
    };

    const handleTouchStart = () => {
      isUsingTouch = true;
    };

    const handleMouseEnter = (e: MouseEvent) => {
      const { target } = e;

      if (!(target instanceof HTMLElement)) {
        close();
        return;
      }

      setTimeout(() => {
        const timeSinceLastMove = Date.now() - lastMouseMoveTime.current;
        const hasRecentMovement = timeSinceLastMove < activeMovementThreshold;

        if (isScrolling || !hasRecentMovement || isUsingTouch) {
          return;
        }

        if (isHoverCardAnchor(target)) {
          cancelLeaveTimeout();
          currentAnchor = target;

          setEnterTimeout(() => {
            open(target);
          }, enterDelay);
        }

        if (target === currentAnchor || target === cardRef.current) {
          cancelLeaveTimeout();
        }
      }, 0);
    };

    const handleMouseLeave = (e: MouseEvent) => {
      const { target } = e;

      if (!currentAnchor) {
        return;
      }

      if (target === currentAnchor || target === cardRef.current) {
        cancelEnterTimeout();

        setLeaveTimeout(() => {
          close();
        }, leaveDelay);
      }
    };

    const handleScrollEnd = () => {
      isScrolling = false;
    };

    const handleScroll = () => {
      isScrolling = true;
      cancelEnterTimeout();
      setScrollTimeout(handleScrollEnd, 100);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isUsingTouch) {
        isUsingTouch = false;
      }

      const hasMoved =
        Math.max(Math.abs(e.movementX), Math.abs(e.movementY)) > 0;

      if (!hasMoved) {
        return;
      }

      delayEnterTimeout(enterDelay);
      lastMouseMoveTime.current = Date.now();
    };

    document.body.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });

    document.body.addEventListener('mouseenter', handleMouseEnter, {
      passive: true,
      capture: true,
    });

    document.body.addEventListener('mousemove', handleMouseMove, {
      passive: true,
      capture: false,
    });

    document.body.addEventListener('mouseleave', handleMouseLeave, {
      passive: true,
      capture: true,
    });

    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      document.body.removeEventListener('touchstart', handleTouchStart);
      document.body.removeEventListener('mouseenter', handleMouseEnter);
      document.body.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [
    setEnterTimeout,
    setLeaveTimeout,
    setScrollTimeout,
    cancelEnterTimeout,
    cancelLeaveTimeout,
    delayEnterTimeout,
    setOpen,
    setStatusId,
    setAnchor,
  ]);

  return (
    <Popover
      onClose={handleClose}
      isOpen={open}
      reference={anchor}
      placement='bottom-start'
      offset={offset}
    >
      {({ props }) => (
        <div {...props} className='visibility-hover-card-controller'>
          <StatusVisibilityHoverCard statusId={statusId} ref={cardRef} />
        </div>
      )}
    </Popover>
  );
};
